import React, { useEffect, useState } from 'react';
import Header from '../../layouts/Header';
import {
    Table,
    Button,
    OverlayTrigger,
    Tooltip,
    Modal,
    Row,
    Col,
    Form,
    Collapse,
    Spinner
} from 'react-bootstrap';
import Select from 'react-select';
import { PencilFill, EyeSlashFill, EyeFill, Download } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axios';
import makeAnimated from "react-select/animated";
import { useSelector } from 'react-redux';

const ProductAddonAllocation = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem("authToken");
    const animatedComponents = makeAnimated();
    const { access } = useSelector((state) => state.login);
    const { loginEmail } = useSelector((state) => state.login);
    const email = loginEmail || localStorage.getItem("email");

    const [menuAccess, setMenuAccess] = useState([]);

    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("AddonAllocation:")
            );
            setMenuAccess(filteredAccess);
        }
    }, [access]);

    const [allocations, setAllocations] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [addonsList, setAddonsList] = useState([]);
    const [outlets, setOutlets] = useState([]);
    const [showModal, setShowModal] = useState(false);
    // For expanding/collapsing a row
    const [expandedRow, setExpandedRow] = useState(null);
    const [isDeleteBtnLoading, setIsDeleteBtnLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [isAddBtnLoading, setIsAddBtnLoading] = useState(false);

    // Filter states for product and addon group (title)
    const [filterProduct, setFilterProduct] = useState(null);
    const [filterAddon, setFilterAddon] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [addonData, setAddonData] = useState(null);
    const [isEditBtnLoading, setIsEditBtnLoading] = useState(false);

    // Tracks which addon-value rows have their "exclude outlets" panel open
    const [openOutletRows, setOpenOutletRows] = useState({});

    const [formData, setFormData] = useState({
        product: null,
        addon: null,
        excludedValues: [],
        excludedOutlets: [],
        groupStatus: "INACTIVE",
        values: []
    });

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            minWidth: "200px",
            borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(13,110,253,.15)" : null,
            "&:hover": {
                borderColor: state.isFocused ? "#86b7fe" : "#ced4da"
            }
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d"
        })
    };

    // Map outlets to react-select options
    const outletData = outlets.map((outlet) => ({
        value: outlet._id,
        label: `${outlet.name} (${outlet.city})`
    }));

    const resetFormData = () => ({
        product: null,
        addon: null,
        excludedValues: [],
        excludedOutlets: [],
        groupStatus: "INACTIVE",
        values: []
    });

    const handleOpenAdd = () => {
        setFormData(resetFormData());
        setAddonData(null);
        setOpenOutletRows({});
        setShowModal(true);
    };

    // --- Data fetchers ---
    const getProductAddons = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("addon-product-allocation", { headers });
            if (response?.data?.status) {
                const fetchedAllocations = response?.data?.data?.data || [];
                setAllocations(fetchedAllocations);
            }
        } catch (error) {
            toast.error("Failed to fetch addon product allocations", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
        }
    };

    const getProductsList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("product", { headers });
            if (response?.data?.status) {
                setProductsList(response?.data?.data?.products);
            }
        } catch (error) {
            toast.error("Failed to fetch products", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
        }
    };

    const getAddonsList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("addons?page=1&limit=50", { headers });
            if (response?.data?.status) {
                setAddonsList(response?.data?.data?.addons);
            }
        } catch (error) {
            toast.error("Failed to fetch addons", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
        }
    };

    // Fetch outlets
    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("outlet", { headers });
            if (response?.data?.status === true) {
                const filteredOutlets = response.data.data.outlets.filter(
                    (act) => act.status === "ACTIVE"
                );
                setOutlets(filteredOutlets);
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error.response?.status === 406) {
                    errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                } else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.error;
                }
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    // Download Addon Product Allocations
    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            let url = `addon-product-allocation?isDownload=true`;

            const response = await axiosInstance.get(url, {
                headers,
                responseType: 'arraybuffer'
            });

            const contentType = response.headers['content-type'] || '';

            if (contentType.includes('application/json')) {
                const decoder = new TextDecoder('utf-8');
                const text = decoder.decode(new Uint8Array(response.data));
                const errorData = JSON.parse(text);
                toast.error(errorData.error || errorData.message || "Failed to download allocations");
                setDownloadLoading(false);
                return;
            }

            const date = new Date();
            const timestamp = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}${String(date.getMinutes()).padStart(2, '0')}${String(date.getSeconds()).padStart(2, '0')}`;

            let filename = `Addon_Product_Allocations_${timestamp}`;

            if (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
                filename += '.xlsx';
            } else if (contentType.includes('application/vnd.ms-excel')) {
                filename += '.xls';
            } else if (contentType.includes('text/csv') || contentType.includes('application/csv')) {
                filename += '.csv';
            } else {
                filename += '.xlsx';
            }

            const blob = new Blob([response.data], {
                type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            if (blob.size === 0) {
                toast.error("Downloaded file is empty");
                setDownloadLoading(false);
                return;
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            }, 200);

            toast.success("Addon product allocations downloaded successfully!", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } catch (error) {
            if (error.response?.data instanceof ArrayBuffer || error.response?.data instanceof Uint8Array) {
                try {
                    const decoder = new TextDecoder('utf-8');
                    const text = decoder.decode(new Uint8Array(error.response.data));
                    const errorData = JSON.parse(text);
                    toast.error(errorData.error || errorData.message || "Failed to download allocations");
                } catch {
                    toast.error("Failed to download allocations. The server returned an unexpected response.");
                }
            } else if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error("Failed to download allocations. Please try again.");
            }
        } finally {
            setDownloadLoading(false);
        }
    };

    useEffect(() => {
        getProductAddons();
        getProductsList();
        getAddonsList();
        getOutlets();
    }, []);

    const handleDelete = async (allocationId) => {
        setIsDeleteBtnLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axiosInstance.delete(`addon-product-allocation?_id=${allocationId}`, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
                getProductAddons();
            }
        } catch (error) {
            if (error.response?.status === 403 || error.response?.status === 404) {
                toast.error(error?.response?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            } else if (error.response && error.response?.data?.status === false) {
                toast.error(error.response.data.error, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setIsDeleteBtnLoading(false);
        }
    };

    const handleToggleRow = (allocationId) => {
        setExpandedRow(expandedRow === allocationId ? null : allocationId);
    };

    const toggleOutletRow = (avId) => {
        setOpenOutletRows((prev) => ({ ...prev, [avId]: !prev[avId] }));
    };

    // --- Form handlers ---
    const handleProductChange = (selectedOption) => {
        setFormData({ ...formData, product: selectedOption });
    };

    const handleAddonChange = (selectedOption) => {
        const selectedAddon = addonsList.find(addon => addon._id === selectedOption.value);
        const autoPopulatedValues = selectedAddon?.addonValues?.map(av => ({
            value: { value: av._id, _id: av._id, label: `${av.sequenceNumber}. ${av.value}` },
            price: '',
            subLabel: '',
            excludedOutlets: [],
            status: av.status,
            isDefault: false
        })) || [];
        setFormData({
            ...formData,
            addon: selectedOption,
            excludedValues: [],
            values: autoPopulatedValues
        });
        setAddonData(selectedAddon || null);
        setOpenOutletRows({});
    };

    const findValueIndex = (values, av) =>
        values.findIndex((v) => v.value?._id === av._id || v.value?.value === av._id);

    // Single toggle for including/excluding an addon value — used by both the
    // create and edit flows. Excluding a value keeps its full row (price,
    // subLabel, isDefault, excludedOutlets, _id) tucked away in excludedValues
    // so re-including it restores everything instead of resetting to blank.
    const handleToggleAddonValue = (av) => {
        setFormData((prev) => {
            const activeIndex = findValueIndex(prev.values, av);

            if (activeIndex !== -1) {
                const removedRow = prev.values[activeIndex];
                const alreadyExcluded = prev.excludedValues.some((opt) => opt.value === av._id);
                const updatedExcluded = alreadyExcluded
                    ? prev.excludedValues
                    : [
                        ...prev.excludedValues,
                        {
                            value: av._id,
                            label: `${av.sequenceNumber}. ${av.value}`,
                            originalRow: removedRow
                        }
                    ];
                return {
                    ...prev,
                    values: prev.values.filter((_, idx) => idx !== activeIndex),
                    excludedValues: updatedExcluded
                };
            }

            const excludedEntry = prev.excludedValues.find((opt) => opt.value === av._id);
            const restoredRow = excludedEntry?.originalRow
                ? { ...excludedEntry.originalRow }
                : {
                    value: { value: av._id, _id: av._id, label: `${av.sequenceNumber}. ${av.value}` },
                    price: '',
                    subLabel: '',
                    status: av.status || "INACTIVE",
                    isDefault: false,
                    excludedOutlets: []
                };

            return {
                ...prev,
                excludedValues: prev.excludedValues.filter((opt) => opt.value !== av._id),
                values: [...prev.values, restoredRow]
            };
        });
    };

    const updateValueField = (av, field, fieldValue) => {
        setFormData((prev) => {
            const idx = findValueIndex(prev.values, av);
            if (idx === -1) return prev;
            const updatedValues = [...prev.values];
            updatedValues[idx] = { ...updatedValues[idx], [field]: fieldValue };
            return { ...prev, values: updatedValues };
        });
    };

    const updateValueOutlets = (av, selected) => {
        updateValueField(av, "excludedOutlets", selected || []);
    };

    const toggleDefaultValue = (av, checked) => {
        setFormData((prev) => ({
            ...prev,
            values: prev.values.map((v) => {
                const matches = v.value?._id === av._id || v.value?.value === av._id;
                if (checked) return { ...v, isDefault: matches };
                return matches ? { ...v, isDefault: false } : v;
            })
        }));
    };

    const handleGroupOutletsChange = (selected) => {
        setFormData((prev) => ({ ...prev, excludedOutlets: selected || [] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.values.length) {
            toast.error("Include at least one value before submitting", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
            return;
        }
        setIsAddBtnLoading(true);
        const payload = {
            product: formData?.product?.value,
            addon: formData?.addon?.value,
            excludedOutlets: formData?.excludedOutlets?.map(ex => ex.value),
            excludedValues: formData?.excludedValues?.map(opt => opt.value),
            status: formData?.groupStatus,
            values: formData?.values?.map(val => ({
                value: val.value?.value,
                price: Number(val.price),
                subLabel: val.subLabel,
                status: val.status,
                excludedOutlets: val?.excludedOutlets?.map(ex => ex.value),
                isDefault: val.isDefault || false
            }))
        };
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.post("addon-product-allocation", payload, { headers });
            if (response?.data?.status) {
                toast.success("Product addon allocation created successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored"
                });
                setFormData(resetFormData());
                setAddonData(null);
                setShowModal(false);
                getProductAddons();
            } else {
                toast.error("Failed to create allocation", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored"
                });
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = "";
                if (error?.response?.status === 406) {
                    errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                } else if (error.response?.status === 400) {
                    errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
                } else if (error.response?.status === 409) {
                    errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
                } else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.error;
                }
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setIsAddBtnLoading(false);
        }
    };

    const openEditAllocationModal = (allocation) => {
        const productOption = { value: allocation?.product?._id, label: allocation?.product?.name };
        const addonOption = { value: allocation?.addon?._id, label: allocation?.addon?.title };

        const mappedValues = allocation?.values?.map(val => ({
            _id: val?._id,
            value: {
                value: val?.value?.value,
                label: `${val?.value?.sequenceNumber || ''}. ${val?.value?.value}`,
                _id: val?.value?._id
            },
            price: val?.price,
            subLabel: val?.subLabel,
            excludedOutlets: val?.excludedOutlets?.map((out) => ({
                value: out._id,
                label: out.name
            })) || [],
            isDefault: val?.isDefault || false
        })) || [];

        const allocatedValueIds = allocation?.values?.map(val => val?.value?._id) || [];
        const allAddonValues = allocation?.addon?.addonValues || [];
        const unallocatedAddonValues = allAddonValues.filter(av => !allocatedValueIds.includes(av._id));

        const manualExcluded = (allocation?.excludedValues || []).map(ev => ({
            value: ev._id || ev.value,
            label: `${ev.sequenceNumber || ''}. ${ev.value || ev.label}`
        }));

        const computedExcluded = unallocatedAddonValues.map(av => ({
            value: av._id,
            label: `${av.sequenceNumber}. ${av.value}`
        }));

        const mergedExcluded = [...manualExcluded];
        computedExcluded.forEach((ce) => {
            if (!mergedExcluded.find(m => m.value === ce.value)) {
                mergedExcluded.push(ce);
            }
        });

        setFormData({
            _id: allocation?._id,
            product: productOption,
            addon: addonOption,
            groupStatus: allocation?.addon?.status,
            excludedValues: mergedExcluded,
            excludedOutlets: allocation?.excludedOutlets?.map((out) => ({
                value: out?._id,
                label: out?.name
            })) || [],
            values: mappedValues
        });
        setAddonData(allocation?.addon);
        setOpenOutletRows({});
        setShowEditModal(true);
    };

    const handleEditAllocationSubmit = async (e) => {
        e.preventDefault();
        if (!formData.values.length) {
            toast.error("Include at least one value before saving", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
            return;
        }
        setIsEditBtnLoading(true);
        const payload = {
            _id: formData?._id,
            product: formData.product?.value,
            addon: formData.addon?.value,
            excludedOutlets: formData.excludedOutlets.length > 0
                ? formData.excludedOutlets.map(opt => opt.value)
                : null,
            excludedValues: formData.excludedValues.map(opt => opt.value),
            status: formData.groupStatus,
            values: formData.values.map(val => ({
                _id: val?._id,
                value: val.value?._id,
                excludedOutlets: val?.excludedOutlets?.length > 0
                    ? val?.excludedOutlets?.map(opt => opt?.value)
                    : null,
                price: Number(val.price),
                subLabel: val.subLabel,
                status: val.status,
                isDefault: val.isDefault || false
            }))
        };
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.put(`addon-product-allocation`, payload, { headers });
            if (response?.data?.status) {
                toast.success("Addon allocation updated successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored"
                });
                setShowEditModal(false);
                getProductAddons();
            } else {
                toast.error("Failed to update addon allocation", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored"
                });
            }
        } catch (error) {
            toast.error("Error updating addon allocation", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored"
            });
        } finally {
            setIsEditBtnLoading(false);
        }
    };

    // --- Filter Options ---
    const productOptions = productsList?.map(prod => ({
        value: prod._id,
        label: prod.name
    }));

    const addonOptions = addonsList?.map(addon => ({
        value: addon._id,
        label: addon.title
    }));

    // --- Filtering the allocations ---
    const filteredAllocations = allocations?.filter(allocation => {
        const matchProduct = filterProduct ? allocation?.product?._id === filterProduct?.value : true;
        const matchAddon = filterAddon ? allocation?.addon?._id === filterAddon?.value : true;
        return matchProduct && matchAddon;
    });

    // --- Group the filtered allocations by addon title ---
    const groupedAllocations = filteredAllocations?.reduce((acc, allocation) => {
        const key = allocation?.addon?.title;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(allocation);
        return acc;
    }, {});

    // --- Shared "Values" editor, used by both the create and edit modals ---
    const renderAllocatedValues = () => {
        if (!addonData) return null;

        const sortedAddonValues = [...(addonData?.addonValues || [])].sort(
            (a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0)
        );

        if (!sortedAddonValues.length) {
            return <p className="text-muted small mb-0">This addon has no values yet.</p>;
        }

        return (
            <div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="fw-semibold mb-0">Values</h6>
                    <span className="text-muted small">
                        {formData.values.length} of {sortedAddonValues.length} included
                    </span>
                </div>
                <div className="d-flex flex-column gap-2">
                    {sortedAddonValues.map((av) => {
                        const activeRow = formData.values.find(
                            (v) => v.value?._id === av._id || v.value?.value === av._id
                        );
                        const isActive = !!activeRow;
                        const outletsOpen = !!openOutletRows[av._id];
                        const outletCount = activeRow?.excludedOutlets?.length || 0;

                        return (
                            <div
                                key={av._id}
                                className="border rounded"
                                style={{
                                    padding: "0.65rem 0.9rem",
                                    backgroundColor: isActive ? "#ffffff" : "#f8f9fa",
                                    transition: "background-color 0.15s ease"
                                }}
                            >
                                <div className="d-flex align-items-center justify-content-between">
                                    <Form.Check
                                        type="switch"
                                        id={`av-toggle-${av._id}`}
                                        checked={isActive}
                                        onChange={() => handleToggleAddonValue(av)}
                                        label={
                                            <span className={isActive ? "fw-medium" : "text-muted"}>
                                                {av.sequenceNumber}. {av.value}
                                            </span>
                                        }
                                    />
                                    {isActive && activeRow?.isDefault && (
                                        <span className="text-primary small fw-semibold">Default</span>
                                    )}
                                </div>

                                <Collapse in={isActive}>
                                    <div>
                                        <div className="mt-2 ps-4">
                                            <Row className="g-2 align-items-center">
                                                <Col xs={6} md={3}>
                                                    <Form.Control
                                                        size="sm"
                                                        type="number"
                                                        step="any"
                                                        value={activeRow?.price ?? ''}
                                                        onChange={(e) => updateValueField(av, "price", e.target.value)}
                                                        placeholder="Price"
                                                        required={isActive}
                                                    />
                                                </Col>
                                                <Col xs={6} md={3}>
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        value={activeRow?.subLabel ?? ''}
                                                        onChange={(e) => updateValueField(av, "subLabel", e.target.value)}
                                                        placeholder="Sub label"
                                                    />
                                                </Col>
                                                <Col xs={6} md={3}>
                                                    <Form.Check
                                                        type="switch"
                                                        id={`av-default-${av._id}`}
                                                        label="Set as default"
                                                        checked={activeRow?.isDefault || false}
                                                        onChange={(e) => toggleDefaultValue(av, e.target.checked)}
                                                    />
                                                </Col>
                                                <Col xs={6} md={3} className="text-md-end">
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        className="text-muted p-0"
                                                        onClick={() => toggleOutletRow(av._id)}
                                                    >
                                                        {outletsOpen
                                                            ? "Hide outlet exceptions"
                                                            : outletCount
                                                                ? `${outletCount} outlet(s) excluded`
                                                                : "Exclude for outlets"}
                                                    </Button>
                                                </Col>
                                            </Row>
                                            <Collapse in={outletsOpen}>
                                                <div className="mt-2">
                                                    <Select
                                                        isMulti
                                                        options={outletData}
                                                        value={activeRow?.excludedOutlets || []}
                                                        onChange={(selected) => updateValueOutlets(av, selected)}
                                                        components={animatedComponents}
                                                        styles={selectStyles}
                                                        placeholder="Exclude specific outlets for this value..."
                                                    />
                                                </div>
                                            </Collapse>
                                        </div>
                                    </div>
                                </Collapse>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4" style={{ backgroundColor: "#f4f5f7", minHeight: "100vh" }}>
                <div className="mb-4">
                    <h5 className="fw-semibold mb-1">ADDON MENU ALLOCATION</h5>
                    <p className="text-muted small mb-0">Control which addon values are available on each menu item, and at what price.</p>
                </div>

                {/* Toolbar */}
                <div className="bg-white border rounded-3 shadow-sm p-3 mb-4">
                    <Row className="align-items-end g-2">
                        <Col md={4}>
                            <Form.Label className="small text-muted fw-semibold  mb-1">Menu</Form.Label>
                            <Select
                                options={productOptions}
                                value={filterProduct}
                                onChange={setFilterProduct}
                                placeholder="All menus"
                                isClearable
                                styles={selectStyles}
                            />
                        </Col>
                        <Col md={4}>
                            <Form.Label className="small text-muted fw-semibold mb-1">Addon group</Form.Label>
                            <Select
                                options={addonOptions}
                                value={filterAddon}
                                onChange={setFilterAddon}
                                placeholder="All addon groups"
                                isClearable
                                styles={selectStyles}
                            />
                        </Col>
                        <Col md={4} className="text-end">
                            <div className="d-flex gap-2 justify-content-end">
                                {(menuAccess.includes("AddonAllocation:get") || email === "contact@xpansecafe.com") && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleDownload}
                                        disabled={downloadLoading}
                                        className="d-flex align-items-center gap-1"
                                    >
                                        {downloadLoading ? <Spinner size="sm" animation="border" /> : <Download size={15} />}
                                        {downloadLoading ? "Downloading..." : "Download"}
                                    </Button>
                                )}
                                {(menuAccess.includes("AddonAllocation:add") || email === "contact@xpansecafe.com") && (
                                    <Button variant="primary" onClick={handleOpenAdd} className="shadow-sm">
                                        New Allocation
                                    </Button>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>

                <div>
                    {filteredAllocations?.length > 0 ? (
                        Object.keys(groupedAllocations)?.map(group => (
                            <div key={group} className="mb-4">
                                {(menuAccess.includes("AddonAllocation:get") || email === "contact@xpansecafe.com") && (
                                    <div className="bg-white border rounded-3 shadow-sm overflow-hidden">
                                        <div
                                            className="d-flex align-items-center justify-content-between px-3 py-3"
                                            style={{ borderBottom: "1px solid #eef0f2" }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                <span
                                                    style={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: "50%",
                                                        display: "inline-block",
                                                        backgroundColor: groupedAllocations[group][0]?.addon?.status === "ACTIVE" ? "#198754" : "#adb5bd"
                                                    }}
                                                />
                                                <h6 className="fw-semibold mb-0">{group}</h6>
                                                <span
                                                    className={`badge rounded-pill ${groupedAllocations[group][0]?.addon?.status === "ACTIVE" ? "text-bg-success-subtle text-success" : "text-bg-light text-muted border"}`}
                                                >
                                                    {groupedAllocations[group][0]?.addon?.status}
                                                </span>
                                            </div>
                                            <span className="text-muted small">
                                                {groupedAllocations[group].length} menu item{groupedAllocations[group].length !== 1 ? "s" : ""}
                                            </span>
                                        </div>
                                        <Table hover responsive className="align-middle mb-0">
                                            <thead>
                                                <tr className="text-muted small text-uppercase">
                                                    <th className="border-top-0 ps-3">Menu</th>
                                                    <th className="border-top-0">Category</th>
                                                    <th className="border-top-0 text-end pe-3">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupedAllocations[group].map(allocation => (
                                                    <React.Fragment key={allocation._id}>
                                                        <tr>
                                                            <td className='fw-semibold ps-3'>{allocation?.product?.name}</td>
                                                            <td>
                                                                {allocation?.product?.category?.name && (
                                                                    <span className="badge rounded-pill text-bg-light text-muted border fw-normal">
                                                                        {allocation.product.category.name}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-end pe-3">
                                                                {(menuAccess.includes("AddonAllocation:update") || email === "contact@xpansecafe.com") && (
                                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEditAllocationModal(allocation)}>
                                                                            <PencilFill size={14} />
                                                                        </Button>
                                                                    </OverlayTrigger>
                                                                )}
                                                                {(menuAccess.includes("AddonAllocation:get") || email === "contact@xpansecafe.com") && (
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        className="d-inline-flex align-items-center gap-1"
                                                                        onClick={() => handleToggleRow(allocation._id)}
                                                                    >
                                                                        {expandedRow === allocation?._id ? <EyeSlashFill size={14} /> : <EyeFill size={14} />}
                                                                        {expandedRow === allocation?._id ? 'Hide' : 'View'}
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan="3" className="p-0 border-0">
                                                                <Collapse in={expandedRow === allocation._id}>
                                                                    <div>
                                                                        <div className="px-3 pb-3">
                                                                            <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                                                                <h6 className="mb-3">{allocation.addon.title}</h6>
                                                                                {allocation?.addon?.addonValues && allocation?.addon?.addonValues.map(av => {
                                                                                    const allocated = allocation?.values?.find(val => val?.value?._id === av._id);
                                                                                    return (
                                                                                        <div key={av?._id} className="d-flex align-items-center justify-content-between mb-2 p-2 bg-white border rounded">
                                                                                            <div>
                                                                                                <span className={allocated ? "fw-medium" : "text-muted"}>
                                                                                                    {av?.sequenceNumber}. {av?.value}
                                                                                                </span>
                                                                                                {!allocated && (
                                                                                                    <span className="badge text-bg-light border text-muted ms-2">Unallocated</span>
                                                                                                )}
                                                                                            </div>
                                                                                            {allocated && (
                                                                                                <div className="text-end small">
                                                                                                    {allocated?.isDefault && (
                                                                                                        <span className="text-primary fw-semibold me-2">Default</span>
                                                                                                    )}
                                                                                                    <span className="me-2">₹{allocated.price}</span>
                                                                                                    {allocated?.subLabel && <span className="text-muted">({allocated?.subLabel})</span>}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Collapse>
                                                            </td>
                                                        </tr>
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-white border rounded-3 shadow-sm py-5">
                            <p className="text-center text-muted mb-0">No addon menu allocations yet.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* New Allocation Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered scrollable>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title as="h6" className="fw-semibold">Create Menu Addon Allocation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group controlId="selectProduct">
                                    <Form.Label className='small fw-semibold'>Menu <span className='text-danger'>*</span></Form.Label>
                                    <Select
                                        options={productOptions}
                                        value={formData.product}
                                        onChange={handleProductChange}
                                        placeholder="Select menu..."
                                        styles={selectStyles}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="selectAddon">
                                    <Form.Label className='small fw-semibold'>Addon <span className='text-danger'>*</span></Form.Label>
                                    <Select
                                        options={addonOptions}
                                        value={formData.addon}
                                        onChange={handleAddonChange}
                                        placeholder="Select addon..."
                                        styles={selectStyles}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group controlId="groupExcludedOutlets">
                                    <Form.Label className='small fw-semibold'>Excluded outlets (applies to all values)</Form.Label>
                                    <Select
                                        isMulti
                                        options={outletData}
                                        value={formData.excludedOutlets}
                                        onChange={handleGroupOutletsChange}
                                        components={animatedComponents}
                                        styles={selectStyles}
                                        placeholder="None — available everywhere"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        {formData.addon && (
                            <>
                                <hr />
                                {renderAllocatedValues()}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={isAddBtnLoading}>
                            {isAddBtnLoading ? <Spinner size="sm" /> : "Create Allocation"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Allocation Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered scrollable>
                <Form onSubmit={handleEditAllocationSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title as="h6" className="fw-semibold">Edit Menu Addon Allocation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="g-3 mb-1">
                            <Col md={6}>
                                <div className="small text-muted mb-1">Menu</div>
                                <div className="fw-semibold">{formData?.product?.label || "—"}</div>
                            </Col>
                            <Col md={6}>
                                <div className="small text-muted mb-1">Addon</div>
                                <div className="fw-semibold">{formData?.addon?.label || "—"}</div>
                            </Col>
                            <Col md={12}>
                                <Form.Group controlId="editGroupExcludedOutlets">
                                    <Form.Label className='small fw-semibold'>Excluded outlets (applies to all values)</Form.Label>
                                    <Select
                                        isMulti
                                        options={outletData}
                                        value={formData.excludedOutlets}
                                        onChange={handleGroupOutletsChange}
                                        components={animatedComponents}
                                        styles={selectStyles}
                                        placeholder="None — available everywhere"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr />
                        {renderAllocatedValues()}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={isEditBtnLoading}>
                            {isEditBtnLoading ? <Spinner size='sm' /> : "Save Changes"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </React.Fragment>
    );
};

export default ProductAddonAllocation;